import { cupSizeOrder, sortSizesComparator } from "~/utils/sizeSplit"

// `Sku.sizeDimension1/2` come only from ingestion and are the single source of truth: nothing is
// read from the combined size, because tenants disagree on the order (32/30 is waist/length for
// some, length/waist for others). Ingestion contract: dimension 1 = waist / band / width,
// dimension 2 = length / cup / length. Labels stay generic so the UI can't show a wrong name.

export type SizeDimensionInput = {
  size: string | null
  sizeDimension1?: string | null
  sizeDimension2?: string | null
}

export type SizeDimensions = { dimension1: string | null; dimension2: string | null }

export type SizeGrid = {
  bySize: Record<string, SizeDimensions>
  dimension1Values: string[]
  dimension2Values: string[]
}

export const SIZE_DIMENSION_1_LABEL = "Size dimension 1"
export const SIZE_DIMENSION_2_LABEL = "Size dimension 2"

// Named leg lengths, shortest first. Aliases share a rank.
const NAMED_LENGTH_RANKS: [string[], number][] = [
  [["shorts"], 0],
  [["long shorts", "long short"], 1],
  [["knickers"], 2],
  [["capri"], 3],
  [["3/4"], 4],
  [["7/8"], 5],
  [["cropped"], 6],
  [["ankle"], 7],
  [["short"], 8],
  [["regular", "reg.", "reg"], 9],
  [["long"], 10],
  [["extra long", "x-long", "xlong"], 11],
  [["tall"], 12],
]
const namedLengthRank = new Map(
  NAMED_LENGTH_RANKS.flatMap(([aliases, rank]) => aliases.map((a) => [a, rank] as const)),
)

// Sort key only; values are shown as sent. "32", "32.5", "32'", "32\"", "140cm", "W32", "L32"
const numericValueRegex = /^[wl]?\s*(\d+(?:[.,]\d+)?)\s*(?:''|'|"|′|″|cm)?$/i
const toNumber = (value: string) => {
  const match = value.trim().match(numericValueRegex)
  return match?.[1] ? Number(match[1].replace(",", ".")) : null
}

// Numbers first, then cups, then named lengths, then everything else in size order.
function valueRank(value: string): [group: number, rank: number] | null {
  const number = toNumber(value)
  if (number !== null) return [0, number]
  const cup = cupSizeOrder.indexOf(value)
  if (cup !== -1) return [1, cup]
  const length = namedLengthRank.get(value.trim().toLowerCase())
  if (length !== undefined) return [2, length]
  return null
}

export function compareSizeDimensionValues(a: string, b: string) {
  const rankA = valueRank(a)
  const rankB = valueRank(b)
  if (rankA && rankB) return rankA[0] - rankB[0] || rankA[1] - rankB[1] || a.localeCompare(b)
  if (rankA) return -1
  if (rankB) return 1
  return sortSizesComparator(a, b)
}

function sortedUnique(values: Iterable<string | null>) {
  return [...new Set([...values].filter((v): v is string => v !== null))].sort(
    compareSizeDimensionValues,
  )
}

// A size's value is the one its SKUs agree on; none or a conflict leaves it empty, whatever the
// row order (variants combined on a product page can share a size).
const agreedValue = (values: Set<string>) => (values.size === 1 ? [...values][0]! : null)

/**
 * The ingested size dimensions of a set of sizes that belong together (a variant, or the variants
 * combined on a product page), or null when none of them has any.
 */
export function resolveSizeGrid(entries: SizeDimensionInput[]): SizeGrid | null {
  if (!entries.some((e) => e.sizeDimension1 != null || e.sizeDimension2 != null)) return null
  const valuesBySize = new Map<string, [Set<string>, Set<string>]>()
  for (const entry of entries) {
    if (entry.size === null) continue
    let values = valuesBySize.get(entry.size)
    if (!values) {
      values = [new Set(), new Set()]
      valuesBySize.set(entry.size, values)
    }
    if (entry.sizeDimension1 != null) values[0].add(entry.sizeDimension1)
    if (entry.sizeDimension2 != null) values[1].add(entry.sizeDimension2)
  }
  const bySize: Record<string, SizeDimensions> = {}
  for (const [size, [dimension1, dimension2]] of valuesBySize) {
    bySize[size] = { dimension1: agreedValue(dimension1), dimension2: agreedValue(dimension2) }
  }
  return {
    bySize,
    dimension1Values: sortedUnique(Object.values(bySize).map((d) => d.dimension1)),
    dimension2Values: sortedUnique(Object.values(bySize).map((d) => d.dimension2)),
  }
}

/**
 * Whether the split can be drawn as a matrix: every size has both dimensions and each dimension
 * has at least two values (a single length is just a size run).
 */
export function isCompleteSizeGrid(grid: SizeGrid | null | undefined): grid is SizeGrid {
  if (!grid || grid.dimension1Values.length < 2 || grid.dimension2Values.length < 2) return false
  const sizes = Object.values(grid.bySize)
  return sizes.length > 0 && sizes.every((d) => d.dimension1 !== null && d.dimension2 !== null)
}
